import { useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { type ContractTypeRow, useSaveContractTypePlaybook } from "../api";
import { type ContractTypePlaybook } from "./content";

/**
 * Admin editor for a contract type's playbook. Edits the narrative fields with
 * proper inputs; the structured bits (lifecycle, must-have chips, key points,
 * callouts, badge) are CARRIED THROUGH unchanged so editing prose never wipes
 * them — structured-field editing is a planned follow-up. Saves the full
 * resolved playbook so the stored override is self-contained.
 */
export function EditPlaybookForm({
  row,
  current,
  onDone,
}: {
  row: ContractTypeRow;
  current: ContractTypePlaybook;
  onDone: () => void;
}) {
  const save = useSaveContractTypePlaybook();
  const [summary, setSummary] = useState(current.summary ?? "");
  const [whenToUse, setWhenToUse] = useState(current.whenToUse ?? "");
  const [billingStart, setBillingStart] = useState(current.billingStart ?? "");
  const [termRenewal, setTermRenewal] = useState(current.termRenewal ?? "");
  const [howItEnds, setHowItEnds] = useState((current.howItEnds ?? []).join("\n"));
  const [oBilling, setOBilling] = useState(current.outputs?.billingStart ?? "");
  const [oChurn, setOChurn] = useState(current.outputs?.churn ?? "");
  const [oBvb, setOBvb] = useState(current.outputs?.bookedVsBilled ?? "");
  const [oRenewals, setORenewals] = useState(current.outputs?.renewals ?? "");

  const clean = (s: string) => {
    const t = s.trim();
    return t === "" ? undefined : t;
  };

  async function onSave() {
    const ends = howItEnds
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const outputs = {
      billingStart: clean(oBilling),
      churn: clean(oChurn),
      bookedVsBilled: clean(oBvb),
      renewals: clean(oRenewals),
    };
    const hasOutputs = Object.values(outputs).some(Boolean);
    const body: Record<string, unknown> = {
      // Carried through unchanged (structured-field editing is a follow-up).
      label: current.label,
      badge: current.badge,
      lifecycle: current.lifecycle,
      mustHave: current.mustHave,
      keyPoints: current.keyPoints,
      callouts: current.callouts,
      // Edited narrative fields.
      summary: clean(summary),
      whenToUse: clean(whenToUse),
      billingStart: clean(billingStart),
      termRenewal: clean(termRenewal),
      howItEnds: ends.length ? ends : undefined,
      outputs: hasOutputs ? outputs : undefined,
    };
    try {
      await save.mutateAsync({ id: row.id, body });
      toast.success("Playbook saved.");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't save the playbook.");
    }
  }

  const busy = save.isPending;
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Edit playbook — {row.name}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Lifecycle stages, must-have fields, and callouts are preserved as-is; structured editing is
        coming next.
      </p>

      <FormField id="pb-summary" label="Summary">
        <Input id="pb-summary" value={summary} onChange={(e) => setSummary(e.target.value)} disabled={busy} />
      </FormField>
      <FormField id="pb-when" label="When to use it">
        <Textarea id="pb-when" rows={3} value={whenToUse} onChange={(e) => setWhenToUse(e.target.value)} disabled={busy} />
      </FormField>
      <FormField id="pb-billing" label="How billing-start is determined">
        <Textarea id="pb-billing" rows={3} value={billingStart} onChange={(e) => setBillingStart(e.target.value)} disabled={busy} />
      </FormField>
      <FormField id="pb-term" label="Term & renewal">
        <Textarea id="pb-term" rows={3} value={termRenewal} onChange={(e) => setTermRenewal(e.target.value)} disabled={busy} />
      </FormField>
      <FormField id="pb-ends" label="How it ends (one bullet per line)">
        <Textarea id="pb-ends" rows={4} value={howItEnds} onChange={(e) => setHowItEnds(e.target.value)} disabled={busy} />
      </FormField>

      <div className="space-y-3 rounded-md border p-3">
        <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          In the three outputs
        </div>
        <FormField id="pb-o-billing" label="Billing start">
          <Textarea id="pb-o-billing" rows={2} value={oBilling} onChange={(e) => setOBilling(e.target.value)} disabled={busy} />
        </FormField>
        <FormField id="pb-o-churn" label="Churn">
          <Textarea id="pb-o-churn" rows={2} value={oChurn} onChange={(e) => setOChurn(e.target.value)} disabled={busy} />
        </FormField>
        <FormField id="pb-o-bvb" label="Booked-vs-billed">
          <Textarea id="pb-o-bvb" rows={2} value={oBvb} onChange={(e) => setOBvb(e.target.value)} disabled={busy} />
        </FormField>
        <FormField id="pb-o-renewals" label="Renewals">
          <Textarea id="pb-o-renewals" rows={2} value={oRenewals} onChange={(e) => setORenewals(e.target.value)} disabled={busy} />
        </FormField>
      </div>
    </div>
  );
}

function FormField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      {children}
    </div>
  );
}
